/// <amd-module name="ExtensionManagement/ManagementHub/ExtensionsMasterPanel" />
import * as React from "react";

import { IObservableValue } from "VSS/Core/Observable";

import { IListItemDetails, IListSelection, List, ListItem, ListSelection } from "VSSUI/List";
import { bindSelectionToObservable } from "VSSUI/MasterDetailsContext";
import { Observer } from "VSSUI/Observer";
import { Pill, PillVariant } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import { css } from "VSSUI/Util";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";

import { PublisherNameComponent } from "./PublisherNameComponent";
import { IExtensionViewModel, IInstalledExtensionViewModel, TabType } from "./UIContracts";

import * as Resources from "ExtensionManagement/ManagementHub/Resources.ExtManagmentHub";

interface IExtensionsMasterPanelProps {
    /**
     * List of extensions
     */
    extensionList: IExtensionViewModel[];

    /**
     * Aria-label to pass to the List component
     */
    listAriaLabel: string;

    /**
     * Initially selected extension
     */
    selectedExtension: IObservableValue<IExtensionViewModel>;

    /**
     * Selected tab's Id
     */
    selectedTabId: string;
}

/**
 * This class dispalys master panel for master details list.
 */
export class ExtensionsMasterPanelContent extends React.Component<IExtensionsMasterPanelProps> {
    private selection: IListSelection;
    private extensionListProvider: ArrayItemProvider<IExtensionViewModel>;

    constructor(props: IExtensionsMasterPanelProps) {
        super(props);

        this.selection = new ListSelection();
        this.extensionListProvider = new ArrayItemProvider(props.extensionList);
        bindSelectionToObservable(this.selection, this.extensionListProvider, props.selectedExtension);
    }

    public render(): JSX.Element {
        return (
            <List
                ariaLabel={this.props.listAriaLabel}
                itemProvider={this.extensionListProvider}
                selection={this.selection}
                renderRow={this.renderRow}
                width="100%"
            />
        );
    }

    private renderRow = (index: number, item: IExtensionViewModel, details: IListItemDetails<IExtensionViewModel>, key?: string): JSX.Element => {
        if (this.props.selectedTabId === TabType.installed) {
            let installedExtension: IInstalledExtensionViewModel = item as IInstalledExtensionViewModel;

            return (
                <Observer extensionDisabled={installedExtension.extensionDisabled} key={key || "list-item" + index}>
                    {(props: { extensionDisabled: boolean }) => {
                        let pills: JSX.Element | JSX.Element[] = [];
                        if (installedExtension.reauthorizationRequired) {
                            pills.push(
                                <Pill variant={PillVariant.colored} color={{ red: 255, green: 160, blue: 122 }} key="action-required">
                                    {Resources.ActionRequired}
                                </Pill>
                            );
                        }

                        if (props.extensionDisabled) {
                            pills.push(<Pill key="disabled">{Resources.Disabled}</Pill>);
                        }

                        if (pills.length > 1) {
                            pills = <PillGroup className="flex-noshrink">{pills}</PillGroup>;
                        }

                        return this.renderRowContent(index, item, details, key, props.extensionDisabled, pills);
                    }}
                </Observer>
            );
        }

        return this.renderRowContent(index, item, details, key);
    };

    private renderRowContent = (
        index: number,
        item: IExtensionViewModel,
        details: IListItemDetails<IExtensionViewModel>,
        key?: string,
        extensionDisabled?: boolean,
        pills?: JSX.Element | JSX.Element[]
    ): JSX.Element => {
        return (
            <ListItem key={key || "list-item" + index} index={index} details={details}>
                <div className="extensions-master-panel-row flex-row flex-grow scroll-hidden">
                    <img alt="" className={css("extension-icon", extensionDisabled && "disabled-icon")} src={item.extensionIconUrl} />
                    <div className="flex-column flex-grow scroll-hidden">
                        <div className="flex-row flex-grow">
                            <span className="primary-text extension-name text-ellipsis">{item.extensionName}</span>
                            {pills}
                        </div>
                        <PublisherNameComponent
                            className={css("publisher-name secondary-text")}
                            extension={item}
                        />
                    </div>
                </div>
            </ListItem>
        );
    };
}
